import React, { useCallback, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Activity,
  Bot,
  CheckCircle2,
  Database,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  Send,
  Loader2,
  MessageSquare,
  ClipboardList,
  ThumbsUp,
  ThumbsDown,
  ShieldAlert,
} from 'lucide-react';
import { useAuth } from '../lib/auth';

const jobs = [
  'Shift reminders',
  'Stale shift cleanup',
  'Late-start monitoring',
  'Stale patrol cleanup',
  'Shift date integrity',
  'Licence expiry scan',
  'Notification cleanup',
  'Contractor lifecycle reminders',
];

const reviews = [
  ['Authentication integrity', 'Canonical live-account checks passed'],
  ['Tenant isolation', 'Cross-tenant onboarding records excluded'],
  ['Onboarding workflows', 'Create, list, retrieve and update passed'],
  ['Scheduled jobs', 'All eight populated fixtures passed'],
  ['Deployment protection', 'Automatic rollback verified'],
];

const QReviewConsole = () => {
  const { user } = useAuth();
  const [platform, setPlatform] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [checkedAt, setCheckedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qMessage, setQMessage] = useState('');
  const [qSending, setQSending] = useState(false);
  const [qError, setQError] = useState('');
  const [qMessages, setQMessages] = useState([
    {
      role: 'assistant',
      content:
        'Hello, I am Q. Ask me about Rapid X assurance, '
        + 'onboarding, scheduled jobs or deployment readiness.',
    },
  ]);

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [planAction, setPlanAction] = useState('');
  const [showSupersededPlans, setShowSupersededPlans] =
    useState(false);
  const [diagnosticExecutions, setDiagnosticExecutions] =
    useState([]);
  const [diagnosticsLoading, setDiagnosticsLoading] =
    useState(true);
  const [diagnosticAction, setDiagnosticAction] =
    useState('');
  const [diagnosticError, setDiagnosticError] =
    useState('');

  const planRequest = useCallback(async (
    url,
    options = {}
  ) => {
    const token = localStorage.getItem('access_token');

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token
          ? { Authorization: `Bearer ${token}` }
          : {}),
        ...(options.headers || {}),
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.detail || `Guardian returned HTTP ${response.status}`
      );
    }

    return data;
  }, []);

  const loadPlans = useCallback(async () => {
    setPlansLoading(true);

    try {
      const data = await planRequest('/api/q/plans');
      setPlans(data.plans || []);
    } catch (requestError) {
      setQError(
        requestError.message || 'Guardian plans unavailable.'
      );
    } finally {
      setPlansLoading(false);
    }
  }, [planRequest]);

  const draftFormsPlan = async () => {
    setPlanAction('draft');
    setQError('');

    try {
      await planRequest('/api/q/plans/forms-priorities', {
        method: 'POST',
      });
      await loadPlans();
    } catch (requestError) {
      setQError(requestError.message);
    } finally {
      setPlanAction('');
    }
  };

  const decidePlan = async (planId, decision) => {
    setPlanAction(`${decision}:${planId}`);
    setQError('');

    try {
      await planRequest(
        `/api/q/plans/${planId}/${decision}`,
        {
          method: 'POST',
          body: JSON.stringify({
            note:
              decision === 'approve'
                ? 'Approved through Q Review Console'
                : 'Declined through Q Review Console',
          }),
        }
      );

      await loadPlans();
    } catch (requestError) {
      setQError(requestError.message);
    } finally {
      setPlanAction('');
    }
  };

  const loadDiagnosticExecutions = useCallback(async () => {
    setDiagnosticsLoading(true);

    try {
      const data = await planRequest(
        '/api/q/actions/diagnostics/executions'
      );

      setDiagnosticExecutions(
        data.executions || []
      );
    } catch (requestError) {
      setDiagnosticError(
        requestError.message
        || 'Diagnostic receipts are unavailable.'
      );
    } finally {
      setDiagnosticsLoading(false);
    }
  }, [planRequest]);

  const runDiagnostic = async (diagnosticId) => {
    if (diagnosticAction) return;

    setDiagnosticAction(diagnosticId);
    setDiagnosticError('');

    const uniquePart =
      typeof crypto !== 'undefined'
      && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()
            .toString(16)
            .slice(2)}`;

    try {
      const data = await planRequest(
        '/api/q/actions/diagnostics/run',
        {
          method: 'POST',
          body: JSON.stringify({
            diagnostic_id: diagnosticId,
            request_id: `q-console-${uniquePart}`,
          }),
        }
      );

      const execution = data.execution;

      if (
        !execution
        || execution.status !== 'executed_and_verified'
        || execution.result?.verified !== true
        || execution.result?.production_write_performed !== false
      ) {
        throw new Error(
          'Guardian did not return a verified read-only receipt.'
        );
      }

      await loadDiagnosticExecutions();
    } catch (requestError) {
      setDiagnosticError(
        requestError.message
        || 'Diagnostic execution failed.'
      );
    } finally {
      setDiagnosticAction('');
    }
  };

  const refresh = useCallback(async () => {
    setLoading(true);

    const token = localStorage.getItem('access_token');
    const headers = token
      ? { Authorization: `Bearer ${token}` }
      : {};

    const read = async (url) => {
      const response = await fetch(url, { headers });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    };

    const [platformResult, onboardingResult] =
      await Promise.allSettled([
        read('/api/health'),
        read('/api/contractor-onboarding/health'),
      ]);

    setPlatform(
      platformResult.status === 'fulfilled'
        ? platformResult.value
        : { status: 'unavailable' }
    );
    setOnboarding(
      onboardingResult.status === 'fulfilled'
        ? onboardingResult.value
        : { status: 'unavailable' }
    );
    setCheckedAt(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    loadDiagnosticExecutions();
  }, [loadDiagnosticExecutions]);

  const askQ = async (event) => {
    event.preventDefault();

    const message = qMessage.trim();
    if (!message || qSending) return;

    setQError('');
    setQMessage('');
    setQSending(true);
    setQMessages((items) => [
      ...items,
      { role: 'user', content: message },
    ]);

    try {
      const token = localStorage.getItem('access_token');
      const conversationId =
        localStorage.getItem('q_conversation_id');

      const response = await fetch('/api/q/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token
            ? { Authorization: `Bearer ${token}` }
            : {}),
        },
        body: JSON.stringify({
          message,
          conversation_id: conversationId || null,
          page_path: window.location.pathname,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || `HTTP ${response.status}`);
      }

      localStorage.setItem(
        'q_conversation_id',
        data.conversation_id
      );

      setQMessages((items) => [
        ...items,
        { role: 'assistant', content: data.answer },
      ]);
    } catch (error) {
      setQError(error.message || 'Q is temporarily unavailable.');
    } finally {
      setQSending(false);
    }
  };

  const platformHealthy =
    platform?.status === 'healthy' &&
    platform?.mongo_connected === true;

  const onboardingHealthy =
    onboarding?.status === 'healthy';

  const cards = [
    {
      name: 'Rapid X Platform',
      value: platformHealthy ? 'Healthy' : 'Attention required',
      detail: platform?.service || 'Platform health',
      icon: Activity,
      ok: platformHealthy,
    },
    {
      name: 'Database',
      value: platform?.mongo_connected ? 'Connected' : 'Unavailable',
      detail: platform?.db || 'MongoDB',
      icon: Database,
      ok: platform?.mongo_connected,
    },
    {
      name: 'Onboarding',
      value: onboardingHealthy ? 'Healthy' : 'Attention required',
      detail: onboarding?.engine || 'Onboarding engine',
      icon: ShieldCheck,
      ok: onboardingHealthy,
    },
    {
      name: 'Current release',
      value: 'V3.1',
      detail: 'Rollback protected',
      icon: Sparkles,
      ok: true,
    },
  ];

  return (
    <div className="min-h-full bg-[#09090b] text-zinc-100">
      <main className="mx-auto max-w-7xl space-y-6 p-4 md:p-6 lg:p-8">
        <header className="relative overflow-hidden rounded-2xl border border-violet-500/25 bg-gradient-to-br from-violet-950/70 via-zinc-950 to-cyan-950/50 p-6 shadow-2xl">
          <div className="relative flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div className="flex gap-4">
              <div className="h-fit rounded-2xl border border-violet-400/30 bg-violet-500/15 p-3">
                <Bot className="h-8 w-8 text-violet-300" />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-2xl font-bold md:text-3xl">
                    Q Review Console
                  </h1>
                  <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-300">
                    {platformHealthy && onboardingHealthy
                      ? 'Operational'
                      : loading ? 'Checking' : 'Review required'}
                  </span>
                </div>
                <p className="mt-2 text-sm text-zinc-400">
                  Rapid X assurance and deployment oversight
                  {user?.first_name ? ` for ${user.first_name}` : ''}.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={refresh}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-2.5 text-sm font-semibold hover:border-violet-500/60 disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh status
            </button>
          </div>
        </header>

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {cards.map(({ name, value, detail, icon: Icon, ok }) => (
            <article
              key={name}
              className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-5"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                  {name}
                </span>
                <Icon className={`h-5 w-5 ${ok ? 'text-emerald-400' : 'text-amber-400'}`} />
              </div>
              <div className="mt-4 text-xl font-bold">{value}</div>
              <div className="mt-1 text-xs text-zinc-500">{detail}</div>
            </article>
          ))}
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.25fr_0.75fr]">
          <article className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-6">
            <h2 className="text-lg font-semibold">Latest assurance review</h2>
            <p className="mt-1 text-xs text-zinc-500">
              Authentication and onboarding · 14 September 2026
            </p>

            <div className="mt-5 divide-y divide-zinc-800">
              {reviews.map(([name, detail]) => (
                <div
                  key={name}
                  className="flex items-center justify-between gap-4 py-4"
                >
                  <div>
                    <div className="text-sm font-medium">{name}</div>
                    <div className="mt-1 text-xs text-zinc-500">{detail}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 text-sm text-emerald-300">
                    <CheckCircle2 className="h-4 w-4" />
                    Passed
                  </div>
                </div>
              ))}
            </div>
          </article>

          <div className="space-y-6">
            <article className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-5">
              <h2 className="font-semibold">Live status checked</h2>
              <p className="mt-3 text-sm text-zinc-300">
                {checkedAt
                  ? checkedAt.toLocaleString('en-AU')
                  : 'Checking now…'}
              </p>
              <p className="mt-1 text-xs text-zinc-500">
                Platform, database and onboarding
              </p>
            </article>

            <article className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-5">
              <div className="flex items-center gap-2">
                <RotateCcw className="h-5 w-5 text-amber-400" />
                <h2 className="font-semibold">Rollback readiness</h2>
              </div>
              <p className="mt-3 text-sm font-semibold text-emerald-300">
                Verified and available
              </p>
              <p className="mt-1 text-xs text-zinc-500">
                V3.1 pre-deployment backup retained
              </p>
            </article>
          </div>
        </section>

        <section className="overflow-hidden rounded-2xl border border-violet-500/25 bg-zinc-950/80">
          <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-violet-500/15 p-2">
                <MessageSquare className="h-5 w-5 text-violet-300" />
              </div>
              <div>
                <h2 className="font-semibold">Talk with Q</h2>
                <p className="text-xs text-zinc-500">
                  Authenticated · tenant-aware · read-only
                </p>
              </div>
            </div>
            <span className="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-3 py-1 text-xs text-emerald-300">
              GPT-5.4 mini
            </span>
          </div>

          <div className="max-h-[480px] space-y-4 overflow-y-auto p-5">
            {qMessages.map((item, index) => (
              <div
                key={`${item.role}-${index}`}
                className={`flex ${
                  item.role === 'user'
                    ? 'justify-end'
                    : 'justify-start'
                }`}
              >
                <div
                  className={`max-w-[88%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-6 ${
                    item.role === 'user'
                      ? 'bg-violet-600 text-white'
                      : 'border border-zinc-800 bg-zinc-900 text-zinc-200'
                  }`}
                >
                  {item.role === 'assistant' ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {item.content}
                    </ReactMarkdown>
                  ) : (
                    item.content
                  )}
                </div>
              </div>
            ))}

            {qSending && (
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                Q is thinking…
              </div>
            )}
          </div>

          {qError && (
            <div className="mx-5 mb-3 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {qError}
            </div>
          )}

          <form
            onSubmit={askQ}
            className="flex gap-3 border-t border-zinc-800 p-4"
          >
            <textarea
              value={qMessage}
              onChange={(event) => setQMessage(event.target.value)}
              onKeyDown={(event) => {
                if (
                  event.key === 'Enter'
                  && !event.shiftKey
                ) {
                  event.preventDefault();
                  event.currentTarget.form?.requestSubmit();
                }
              }}
              rows={2}
              maxLength={4000}
              placeholder="Ask Q about Rapid X…"
              className="min-h-[52px] flex-1 resize-none rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-sm text-white outline-none placeholder:text-zinc-600 focus:border-violet-500"
            />
            <button
              type="submit"
              disabled={!qMessage.trim() || qSending}
              className="inline-flex h-[52px] items-center gap-2 self-end rounded-xl bg-violet-600 px-5 text-sm font-semibold text-white hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              Send
            </button>
          </form>

          <div className="border-t border-zinc-900 px-5 py-3 text-xs text-zinc-600">
            Q cannot make production changes. Actions require a separately
            reviewed Guardian approval.
          </div>
        </section>

        <section className="overflow-hidden rounded-2xl border border-cyan-500/25 bg-zinc-950/80">
          <div className="flex flex-col gap-4 border-b border-zinc-800 px-5 py-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-cyan-500/15 p-2">
                <Activity className="h-5 w-5 text-cyan-300" />
              </div>

              <div>
                <h2 className="font-semibold">
                  Guardian read-only diagnostics
                </h2>
                <p className="text-xs text-zinc-500">
                  Allowlisted checks · authenticated · receipt protected
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={Boolean(diagnosticAction)}
                onClick={() => runDiagnostic('platform_health')}
                className="inline-flex items-center gap-2 rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-4 py-2 text-sm font-semibold text-cyan-200 hover:bg-cyan-500/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {diagnosticAction === 'platform_health' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Activity className="h-4 w-4" />
                )}
                Run Platform Health
              </button>

              <button
                type="button"
                disabled={Boolean(diagnosticAction)}
                onClick={() => runDiagnostic('onboarding_health')}
                className="inline-flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-200 hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {diagnosticAction === 'onboarding_health' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ShieldCheck className="h-4 w-4" />
                )}
                Run Onboarding Health
              </button>

              <button
                type="button"
                disabled={diagnosticsLoading}
                onClick={loadDiagnosticExecutions}
                aria-label="Refresh diagnostic receipts"
                className="inline-flex items-center gap-2 rounded-xl border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-800 disabled:opacity-50"
              >
                <RefreshCw
                  className={`h-4 w-4 ${
                    diagnosticsLoading ? 'animate-spin' : ''
                  }`}
                />
                Refresh
              </button>
            </div>
          </div>

          {diagnosticError && (
            <div className="border-b border-red-500/20 bg-red-500/5 px-5 py-3 text-sm text-red-300">
              {diagnosticError}
            </div>
          )}

          <div className="p-5">
            {diagnosticsLoading ? (
              <div className="flex items-center gap-2 py-8 text-sm text-zinc-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading verified diagnostic receipts…
              </div>
            ) : diagnosticExecutions.length === 0 ? (
              <div className="rounded-xl border border-dashed border-zinc-800 px-5 py-8 text-center">
                <Activity className="mx-auto mb-3 h-7 w-7 text-zinc-600" />
                <p className="text-sm font-medium text-zinc-300">
                  No diagnostic executions recorded
                </p>
                <p className="mt-1 text-xs text-zinc-600">
                  Run an allowlisted health check to create the first receipt.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {diagnosticExecutions.slice(0, 10).map((execution) => {
                  const verified =
                    execution.status === 'executed_and_verified'
                    && execution.result?.verified === true
                    && execution.result?.production_write_performed === false;

                  const health = execution.result?.health || {};

                  return (
                    <article
                      key={execution.execution_id}
                      className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4"
                    >
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-medium text-white">
                              {execution.diagnostic_id === 'platform_health'
                                ? 'Platform Health'
                                : execution.diagnostic_id === 'onboarding_health'
                                  ? 'Onboarding Health'
                                  : execution.diagnostic_id}
                            </h3>

                            <span
                              className={`rounded-full border px-2 py-1 text-xs ${
                                verified
                                  ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                                  : 'border-red-500/30 bg-red-500/10 text-red-300'
                              }`}
                            >
                              {verified
                                ? 'Executed & verified'
                                : 'Verification failed'}
                            </span>
                          </div>

                          <p className="mt-2 text-sm text-zinc-400">
                            {health.service
                              || health.engine
                              || 'Rapid X diagnostic'}
                            {' · '}
                            {health.status || execution.status}
                          </p>

                          <p className="mt-1 text-xs text-zinc-600">
                            {execution.execution_id}
                          </p>
                        </div>

                        <div className="text-left text-xs text-zinc-500 md:text-right">
                          <p>
                            {execution.verified_at
                              ? new Date(
                                  execution.verified_at
                                ).toLocaleString()
                              : 'Verification time unavailable'}
                          </p>
                          <p className="mt-1">
                            By {execution.actor_id}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 grid gap-3 md:grid-cols-3">
                        <div className="rounded-lg border border-zinc-800 bg-black/20 p-3">
                          <p className="text-[11px] uppercase tracking-wide text-zinc-600">
                            Data access
                          </p>
                          <p className="mt-1 text-sm text-zinc-300">
                            Metadata only
                          </p>
                        </div>

                        <div className="rounded-lg border border-zinc-800 bg-black/20 p-3">
                          <p className="text-[11px] uppercase tracking-wide text-zinc-600">
                            Production writes
                          </p>
                          <p className="mt-1 text-sm text-emerald-300">
                            None
                          </p>
                        </div>

                        <div className="rounded-lg border border-zinc-800 bg-black/20 p-3">
                          <p className="text-[11px] uppercase tracking-wide text-zinc-600">
                            Receipt SHA-256
                          </p>
                          <p
                            className="mt-1 truncate font-mono text-xs text-zinc-400"
                            title={execution.receipt_sha256}
                          >
                            {execution.receipt_sha256}
                          </p>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>

          <div className="border-t border-zinc-900 px-5 py-3 text-xs text-zinc-600">
            Only registered read-only diagnostics can execute. Form,
            scheduler and configuration changes remain disabled.
          </div>
        </section>

        <section className="rounded-2xl border border-amber-500/20 bg-zinc-950/70 p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              <div className="rounded-xl bg-amber-500/10 p-2">
                <ShieldAlert className="h-5 w-5 text-amber-300" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">
                  Guardian change plans
                </h2>
                <p className="mt-1 text-xs text-zinc-500">
                  Decisions only. Production execution is disabled.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={draftFormsPlan}
              disabled={Boolean(planAction)}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-sm font-semibold text-amber-200 hover:bg-amber-500/20 disabled:opacity-50"
            >
              {planAction === 'draft'
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <ClipboardList className="h-4 w-4" />}
              Draft forms priorities 1–3
            </button>
          </div>

          <div className="mt-5 space-y-4">
            {plansLoading && (
              <div className="flex items-center gap-2 text-sm text-zinc-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading Guardian plans…
              </div>
            )}

            {!plansLoading && plans.length === 0 && (
              <div className="rounded-xl border border-dashed border-zinc-800 p-5 text-sm text-zinc-500">
                No Guardian change plans have been drafted.
              </div>
            )}

            {!plansLoading && plans.some(
              (plan) => plan.display_status === 'superseded'
            ) && (
              <button
                type="button"
                onClick={() =>
                  setShowSupersededPlans((current) => !current)
                }
                className="text-xs font-medium text-zinc-500 hover:text-zinc-300"
              >
                {showSupersededPlans
                  ? 'Hide superseded plans'
                  : `Show superseded plans (${
                      plans.filter(
                        (plan) =>
                          plan.display_status === 'superseded'
                      ).length
                    })`}
              </button>
            )}

            {(showSupersededPlans
              ? plans
              : plans.filter(
                  (plan) =>
                    plan.display_status !== 'superseded'
                )
            ).map((plan) => {
              const pending =
                plan.approval_status === 'pending_approval';
              const superseded =
                plan.display_status === 'superseded';
              const executed =
                plan.execution_status === 'executed_verified'
                && plan.execution_verified === true;

              return (
                <article
                  key={plan.plan_id}
                  className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-5"
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold">
                          {plan.title}
                        </h3>

                        <span className={`rounded-full border px-2.5 py-1 text-xs ${
                          executed
                            ? 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300'
                            : superseded
                              ? 'border-zinc-700 bg-zinc-800/60 text-zinc-400'
                              : pending
                              ? 'border-amber-500/30 bg-amber-500/10 text-amber-300'
                              : plan.approval_status === 'approved'
                              ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                              : 'border-red-500/30 bg-red-500/10 text-red-300'
                        }`}>
                          {executed
                            ? 'Executed & verified'
                            : superseded
                              ? 'Superseded'
                              : plan.approval_status}
                        </span>
                      </div>

                      <p className="mt-2 max-w-3xl text-sm leading-6 text-zinc-400">
                        {plan.summary}
                      </p>

                      <div className="mt-3 text-xs text-zinc-600">
                        Plan {plan.plan_id} · Version {plan.plan_version}
                      </div>

                      <div className="mt-1 break-all text-[10px] text-zinc-700">
                        SHA-256: {plan.checksum_sha256}
                      </div>
                    </div>

                    {pending && (
                      <div className="flex shrink-0 gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            decidePlan(plan.plan_id, 'decline')
                          }
                          disabled={Boolean(planAction)}
                          className="inline-flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-semibold text-red-300 hover:bg-red-500/20 disabled:opacity-50"
                        >
                          <ThumbsDown className="h-4 w-4" />
                          Decline
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            decidePlan(plan.plan_id, 'approve')
                          }
                          disabled={Boolean(planAction)}
                          className="inline-flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-50"
                        >
                          <ThumbsUp className="h-4 w-4" />
                          Approve
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 grid gap-3">
                    {(plan.actions || []).map((action) => (
                      <div
                        key={`${plan.plan_id}-${action.priority}`}
                        className="rounded-xl border border-zinc-800 bg-zinc-950 px-4 py-3"
                      >
                        <div className="text-xs font-semibold uppercase tracking-wide text-violet-300">
                          Priority {action.priority}
                        </div>
                        <div className="mt-1 text-sm text-zinc-300">
                          {action.proposed_change}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 border-t border-zinc-800 pt-3 text-xs text-zinc-500">
                    {superseded ? (
                      <div className="space-y-1">
                        <div className="font-semibold text-zinc-400">
                          Superseded
                        </div>
                        <div className="break-all">
                          Replaced by {plan.superseded_by}
                        </div>
                        <div>
                          Retained as an immutable audit record
                        </div>
                      </div>
                    ) : executed ? (
                      <div className="space-y-1">
                        <div className="font-semibold text-cyan-300">
                          Executed and verified
                        </div>
                        <div>
                          Executed {plan.executed_at
                            ? new Date(plan.executed_at).toLocaleString()
                            : 'at recorded time'}
                          {plan.executed_by
                            ? ` by ${plan.executed_by}`
                            : ''}
                        </div>
                        <div>
                          {plan.changed_template_count || 0}
                          {' '}templates verified · Submissions preserved
                        </div>
                        <div className="break-all text-[10px] text-zinc-700">
                          Receipt SHA-256: {plan.execution_receipt_sha256}
                        </div>
                      </div>
                    ) : (
                      <>
                        Status: {plan.lifecycle_status}
                        {' · '}Executable: No
                      </>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-6">
          <h2 className="text-lg font-semibold">Scheduled-job assurance</h2>
          <p className="mt-1 text-xs text-zinc-500">
            Populated isolated fixtures passed
          </p>

          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {jobs.map((job) => (
              <div
                key={job}
                className="flex items-center gap-2 rounded-xl border border-zinc-800 bg-zinc-900/50 px-3 py-3 text-sm"
              >
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" />
                <span>{job}</span>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default QReviewConsole;
